import React, { useEffect, useMemo, useState } from 'react';
import { storageService, HistoryItem, FeedbackRating } from '../services/storageService';
import { useT, formatRelativeTime } from '../lib/i18n';
import { cn } from '../lib/cn';
import { useToast } from './Toast';

type FilterKey = 'all' | 'tweet' | 'analysis' | 'coaching' | 'reply' | 'inspiration' | 'research';
type DateRange = 'all' | '24h' | '7d' | '30d';

const filterKeys: FilterKey[] = ['all', 'tweet', 'analysis', 'coaching', 'reply', 'inspiration', 'research'];

export const History: React.FC = () => {
  const { t, locale } = useT();
  const { toast } = useToast();
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [dateRange, setDateRange] = useState<DateRange>('all');

  useEffect(() => {
    const load = () => setHistoryItems(storageService.getHistoryItems());
    load();
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'bangerfy_history') load();
    };
    window.addEventListener('storage', onStorage);
    const interval = setInterval(load, 1500);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  const filteredItems = useMemo(() => {
    const threshold = (() => {
      const now = Date.now();
      if (dateRange === '24h') return now - 24 * 60 * 60 * 1000;
      if (dateRange === '7d') return now - 7 * 24 * 60 * 60 * 1000;
      if (dateRange === '30d') return now - 30 * 24 * 60 * 60 * 1000;
      return null;
    })();
    const query = searchQuery.toLowerCase();
    return historyItems
      .filter((item) => filter === 'all' || item.type === filter)
      .filter((item) => {
        const contentMatch = item.content.toLowerCase().includes(query);
        const inputMatch = (item.metadata?.inputText || '').toLowerCase().includes(query);
        return contentMatch || inputMatch;
      })
      .filter((item) => {
        if (!threshold) return true;
        return new Date(item.timestamp).getTime() >= threshold;
      })
      .sort((a, b) => {
        const diff = new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        return sortOrder === 'desc' ? diff : -diff;
      });
  }, [historyItems, filter, searchQuery, sortOrder, dateRange]);

  const deleteItem = (id: string) => {
    if (!window.confirm(t('common.deleteConfirm'))) return;
    const items = storageService.deleteHistoryItem(id);
    setHistoryItems(items);
  };

  const clearAllHistory = () => {
    if (window.confirm(t('common.deleteAllConfirm'))) {
      storageService.clearHistory();
      setHistoryItems([]);
    }
  };

  const exportCSV = () => {
    if (filteredItems.length === 0) {
      toast(t('common.noExportData'), 'warning');
      return;
    }
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const header = ['id', 'type', 'timestamp', 'content', 'score', 'metadata'];
    const rows = filteredItems.map((item) => [
      item.id, item.type, new Date(item.timestamp).toISOString(),
      escape(item.content), item.score != null ? String(item.score) : '',
      escape(item.metadata ? JSON.stringify(item.metadata) : ''),
    ]);
    const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bangerfy_history.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const getTypeBadge = (type: string) => {
    const key = `history.filters.${type}` as const;
    const label = t(key);
    const colorMap: Record<string, string> = {
      tweet: 'bg-brand/10 text-brand',
      analysis: 'bg-accent/10 text-accent',
      coaching: 'bg-warning/10 text-warning',
      reply: 'bg-success/10 text-success',
      inspiration: 'bg-accent/10 text-accent',
      research: 'bg-danger/10 text-danger',
    };
    return { label, color: colorMap[type] || 'bg-text-muted/10 text-text-muted' };
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-[30px] max-w-[1100px] mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">{t('history.title')}</h1>
            <p className="text-sm text-text-muted mt-1">{t('history.subtitle')}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={exportCSV}
              className="px-3 py-2 rounded-lg border border-border bg-surface-2 text-text-primary text-sm hover:border-brand"
            >
              {t('history.exportCSV')}
            </button>
            {historyItems.length > 0 && (
              <button
                onClick={clearAllHistory}
                className="px-3 py-2 rounded-lg bg-danger text-white text-sm border border-danger hover:bg-danger/90"
              >
                {t('history.deleteAll')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-5 sm:mb-6 space-y-3">
        <div className="flex flex-wrap gap-2">
          {filterKeys.map((key) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={cn(
                'px-3 py-2 rounded-full text-sm border transition-colors',
                filter === key
                  ? 'bg-brand text-white border-brand'
                  : 'bg-surface-2 border-border text-text-primary hover:border-brand'
              )}
            >
              {t(`history.filters.${key}`)}
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('history.searchPlaceholder')}
              className="w-full pl-3 pr-3 py-3 rounded-xl bg-surface-2 border border-border text-text-primary text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRange)}
              className="px-3 py-3 rounded-xl bg-surface-2 border border-border text-text-primary text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <option value="all">{t('history.dateRange.all')}</option>
              <option value="24h">{t('history.dateRange.day')}</option>
              <option value="7d">{t('history.dateRange.week')}</option>
              <option value="30d">{t('history.dateRange.month')}</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              className="px-3 py-3 rounded-xl bg-surface-2 border border-border text-text-primary text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <option value="desc">{t('history.sort.newest')}</option>
              <option value="asc">{t('history.sort.oldest')}</option>
            </select>
          </div>
        </div>

        <div className="text-xs text-text-muted">
          {t('history.showing', { filtered: filteredItems.length, total: historyItems.length })}
        </div>
      </div>

      {/* History Items */}
      {filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[320px] bg-surface-2 border border-border rounded-2xl text-center p-6">
          <svg className="w-12 h-12 mb-4 text-text-muted/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12h8M12 8v8" />
          </svg>
          <p className="text-text-muted mb-2">{t('history.emptyState')}</p>
          <p className="text-xs text-text-muted">
            {searchQuery || filter !== 'all' ? t('history.emptyHint') : t('history.emptyDefault')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:gap-4">
          {filteredItems.map((item) => {
            const badge = getTypeBadge(item.type);
            return (
              <div
                key={item.id}
                className="p-4 sm:p-5 bg-surface-2 border border-border rounded-2xl transition-all duration-200 hover:border-brand hover:shadow-[0_10px_30px_rgba(29,161,242,0.15)]"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn('px-2 py-1 rounded-full text-xs font-semibold', badge.color)}>
                      {badge.label}
                    </span>
                    <span className="text-xs text-text-muted">{formatRelativeTime(item.timestamp, locale)}</span>
                    {item.score !== undefined && (
                      <span className="text-xs px-2 py-1 rounded-full bg-surface-1 border border-border text-text-muted">
                        {t('common.score')} {typeof item.score === 'number' && item.score <= 1 ? (item.score * 100).toFixed(0) : item.score}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigator.clipboard.writeText(item.content)}
                      className="px-3 py-2 rounded-lg bg-surface-1 border border-border text-text-primary text-xs hover:border-brand"
                    >
                      {t('common.copy')}
                    </button>
                    <button
                      onClick={() => deleteItem(item.id)}
                      className="px-3 py-2 rounded-lg border border-danger text-danger text-xs hover:bg-danger/10"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>

                <p className="text-sm sm:text-base leading-relaxed text-text-primary whitespace-pre-wrap mb-2">
                  {item.content}
                </p>

                {/* Feedback buttons (tweet type only) */}
                {item.type === 'tweet' && (
                  <FeedbackButtons item={item} onUpdate={() => setHistoryItems(storageService.getHistoryItems())} />
                )}

                {item.metadata && Object.keys(item.metadata).filter(k => k !== 'breakdown').length > 0 && (
                  <div className="pt-2 border-t border-border text-xs text-text-muted flex flex-wrap gap-2">
                    {Object.entries(item.metadata).map(([key, value]) => (
                      value != null && key !== 'breakdown' && (
                        <span key={key} className="px-2 py-1 rounded-lg bg-surface-1 border border-border">
                          <strong className="text-text-primary mr-1">{key}:</strong>
                          {String(value).slice(0, 120)}
                        </span>
                      )
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─────────── Feedback Buttons ─────────── */

const TrendUpIcon = () => (
  <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12L8 4l6 8" />
  </svg>
);

const TrendDownIcon = () => (
  <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4L8 12l6-8" />
  </svg>
);

const feedbackOptions: { value: FeedbackRating; label: string; labelJa: string; icon: React.ReactNode | null; color: string; activeColor: string }[] = [
  { value: 'banged', label: 'Banged', labelJa: '伸びた', icon: <TrendUpIcon />, color: 'text-text-muted border-border hover:border-success hover:text-success', activeColor: 'text-success border-success bg-success/10' },
  { value: 'normal', label: 'Normal', labelJa: '普通', icon: null, color: 'text-text-muted border-border hover:border-text-secondary hover:text-text-secondary', activeColor: 'text-text-secondary border-text-secondary bg-text-secondary/10' },
  { value: 'flopped', label: 'Flopped', labelJa: '滑った', icon: <TrendDownIcon />, color: 'text-text-muted border-border hover:border-danger hover:text-danger', activeColor: 'text-danger border-danger bg-danger/10' },
];

const FeedbackButtons: React.FC<{ item: HistoryItem; onUpdate: () => void }> = ({ item, onUpdate }) => {
  const { locale } = useT();
  const [showLikes, setShowLikes] = useState(false);
  const [likesInput, setLikesInput] = useState(item.engagementLikes?.toString() || '');

  const handleFeedback = (rating: FeedbackRating) => {
    const newRating = item.feedback === rating ? null : rating;
    storageService.setFeedback(item.id, newRating, item.engagementLikes);
    onUpdate();
    if (newRating === 'banged') setShowLikes(true);
  };

  const handleLikesBlur = () => {
    const likes = parseInt(likesInput);
    if (!isNaN(likes) && likes >= 0) {
      storageService.setFeedback(item.id, item.feedback || 'banged', likes);
      onUpdate();
    }
  };

  return (
    <div className="flex items-center gap-2 mb-2 flex-wrap">
      <span className="text-[11px] text-text-faint mr-1">
        {locale === 'ja' ? '実績:' : 'Result:'}
      </span>
      {feedbackOptions.map((opt) => (
        <button
          key={opt.value}
          onClick={() => handleFeedback(opt.value)}
          className={cn(
            'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all cursor-pointer',
            item.feedback === opt.value ? opt.activeColor : opt.color
          )}
        >
          {opt.icon}
          {locale === 'ja' ? opt.labelJa : opt.label}
        </button>
      ))}
      {(item.feedback === 'banged' || showLikes) && (
        <div className="flex items-center gap-1 ml-1">
          <span className="text-[11px] text-text-faint">♥</span>
          <input
            type="number"
            value={likesInput}
            onChange={(e) => setLikesInput(e.target.value)}
            onBlur={handleLikesBlur}
            onKeyDown={(e) => e.key === 'Enter' && handleLikesBlur()}
            placeholder="0"
            className="w-16 px-2 py-0.5 text-[11px] bg-surface-1 border border-border rounded text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          />
        </div>
      )}
    </div>
  );
};
